import { useState } from 'react';

import { ProfileModel, profileApi } from '@pukka/api';
import { ContentContainer, DrawerContainer, Loader, NoDataDisplay, PageContainer } from '@pukka/ui-components';
import { DefaultActionBar, OutletSelector } from '@pukka/ui-composite';
import { useSnapshot } from '@pukka/valtio';
import { Row } from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';

import banner1 from './assets/banner-1.jpg';
import banner2 from './assets/banner-2.jpg';
import banner3 from './assets/banner-3.jpg';
import { DetailedCampaignWithStatusAndLoading, State, _PersistedState } from './CustomerDisplay.state';
import CustomerDisplayCreateBox from './CustomerDisplayCreateBox';
import CustomerDisplayEditBox from './CustomerDisplayEditBox';
import { CustomerDisplayTable } from './CustomerDisplayTable';
import { StatusSelector } from './StatusSelector';
import { getOutletName } from './utils';

const allOutlet: ProfileModel.Outlet = {
  id: undefined,
  name: 'All Outlets',
};

function handleOutletChange(outlet: ProfileModel.Outlet) {
  State.onChangeSelectedOutletId(outlet);
}

async function handleFetchMore() {
  State.onFetchMore();
}

function CustomerDisplayCampaign() {
  const { data: profile } = profileApi.useProfile();
  const { selectedOutletId, campaigns, isLoading } = useSnapshot(State);
  const { status: statusFilter } = useSnapshot(_PersistedState);

  const [actionType, setActionType] = useState<'create' | 'edit' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Row<DetailedCampaignWithStatusAndLoading> | null>(null);

  const { isFetched } = State.useData();

  const campaignData = [...campaigns.values()];

  const outletOptions = [allOutlet].concat(profile?.outlets || []);

  const selectedOutletName = getOutletName(outletOptions, selectedOutletId);

  const handleCloseBox = async () => {
    setActionType(null);
    setSelectedCampaign(null);
  };

  const handleClickCreate = () => {
    setActionType('create');
  };

  const handleClickRow = async (row: Row<DetailedCampaignWithStatusAndLoading>) => {
    if (actionType === 'edit') {
      await handleCloseBox();
    }

    setActionType('edit');
    setSelectedCampaign(row);
  };

  return (
    <PageContainer>
      {isLoading && (
        <div className="absolute z-[100] h-full w-full bg-white/50">
          <Loader size={100} className="absolute left-[calc(50%-42px)] top-[50%] translate-x-[-50%] translate-y-[-50%]" />
        </div>
      )}

      <DefaultActionBar
        pageTitle="Customer Display"
        buttonName={(profile?.outlets || []).length > 0 ? 'Create Campaign' : ''}
        banner={[banner1, banner2, banner3]}
        buttonOnClick={handleClickCreate}
      >
        <OutletSelector
          disabled={!isFetched || (!selectedOutletId && statusFilter === 'all' && campaigns.size < 1)}
          selectedOutlet={{
            key: selectedOutletId,
            value: selectedOutletName,
            meta: {
              key: selectedOutletId,
              value: selectedOutletName,
            },
          }}
          outlets={outletOptions}
          onOutletChange={handleOutletChange}
        />
        <StatusSelector />
      </DefaultActionBar>

      <div className="mt-7 flex">
        {campaigns.size < 1 ? (
          <div className={twMerge('relative h-[320px] transition-all', actionType ? 'w-2/4' : 'w-full')}>
            <NoDataDisplay
              isLoading={!isFetched}
              imageSize={100}
              placeholder={
                <span className="max-w-xl text-center text-sm">
                  This page is dedicated for Customer Display user. Customer Display is a standalone hardware that support customer facing
                  screen. Click Create Campaign if you already have one or{' '}
                  <a
                    className="text-light-blue-500 hover:underline"
                    href="https://www.tokopedia.com/mokapointofsale/moka-ultra-plus"
                    target="_blank"
                    rel="noreferrer"
                  >
                    purchase the device here
                  </a>{' '}
                  if you don&apos;t and want to have one{' '}
                </span>
              }
            />
          </div>
        ) : (
          <ContentContainer className={actionType ? 'w-2/4' : 'w-full'}>
            <CustomerDisplayTable
              isMinimized={!!actionType}
              data={campaignData}
              onRowClick={handleClickRow}
              highlightedRowIndexes={[selectedCampaign?.index ?? -1]}
              onBottomReached={handleFetchMore}
            />
          </ContentContainer>
        )}
        <DrawerContainer open={!!actionType} title={actionType === 'create' ? 'Create Campaign' : 'Edit Campaign'} onClose={handleCloseBox}>
          {actionType === 'create' && <CustomerDisplayCreateBox onClose={handleCloseBox} />}
          {actionType === 'edit' && selectedCampaign && !selectedCampaign.original.isLoading && (
            <CustomerDisplayEditBox campaign={selectedCampaign.original} onClose={handleCloseBox} />
          )}
        </DrawerContainer>
      </div>
    </PageContainer>
  );
}

export { CustomerDisplayCampaign as default };
